import React, { useEffect, useState, useMemo } from "react";
import {
  Button,
  Modal,
  notification,
  Form,
  message,
} from "lib/ui";
import { PlusOutlined, DeleteOutlined } from "lib/icons";
import {
  CircleHelp,
  FileText,
  Layers,
  Pencil,
  Trash2,
} from "lucide-react";
import AddUserForm from "./Add";
import fetchApi from "../../../hooks/ApiFetch";
import {
  AdminPageLayout,
  AdminHero,
  AdminStats,
  AdminSearchBar,
} from "../../../components/admin/AdminPageShell";
import {
  PeopleDataTable,
  ActionBtn,
} from "../../../components/admin/PeopleDataTable";

const FaqPage = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form] = Form.useForm();
  const [search, setSearch] = useState("");
  const [data, setData] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await fetchApi.get("/faqs");
      setData(Array.isArray(response.data) ? response.data : []);
    } catch {
      message.error("Failed to load FAQs");
    }
  };

  const handleAdd = () => {
    setEditingUser(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (row) => {
    setEditingUser(row);
    form.setFieldsValue(row);
    setIsModalVisible(true);
  };

  const handleDelete = (id, title) => {
    Modal.confirm({
      title: "Delete FAQ?",
      icon: <DeleteOutlined />,
      content: title ? `Remove "${title}"` : undefined,
      okText: "Delete",
      cancelText: "Cancel",
      okType: "danger",
      onOk: async () => {
        try {
          await fetchApi.delete(`/faqs/${id}`);
          setData((prev) => prev.filter((item) => item._id !== id));
          notification.success({
            message: "Deleted",
            description: "FAQ removed.",
          });
        } catch {
          notification.error({
            message: "Failed",
            description: "Could not delete FAQ.",
          });
        }
      },
    });
  };

  const handleSave = async (values) => {
    try {
      if (editingUser) {
        await fetchApi.put(`/faqs/${editingUser._id}`, values);
        setData((prev) =>
          prev.map((item) =>
            item._id === editingUser._id ? { ...item, ...values } : item
          )
        );
        notification.success({
          message: "Updated",
          description: "FAQ saved.",
        });
      } else {
        const response = await fetchApi.post("/faqs", values);
        setData((prev) => [
          ...prev,
          { ...values, _id: response.data._id ?? response.data?._id },
        ]);
        notification.success({
          message: "Added",
          description: "FAQ created.",
        });
      }
      setIsModalVisible(false);
      form.resetFields();
    } catch {
      notification.error({
        message: "Failed",
        description: "Could not save FAQ.",
      });
    }
  };

  const filteredData = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((item) => {
      const blob = [item.title, item.shortDescription, item.description]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return blob.includes(q);
    });
  }, [data, search]);

  const columns = [
    {
      key: "title",
      header: "Question",
      render: (row) => (
        <div className="max-w-md">
          <p className="font-semibold text-slate-900">{row.title || "—"}</p>
          {row.shortDescription ? (
            <p className="mt-0.5 line-clamp-2 text-xs text-slate-500">
              {row.shortDescription}
            </p>
          ) : null}
        </div>
      ),
    },
    {
      key: "preview",
      header: "Answer preview",
      render: (row) => (
        <span className="line-clamp-2 max-w-sm text-xs text-slate-600">
          {row.description || "—"}
        </span>
      ),
    },
  ];

  return (
    <AdminPageLayout>
      <AdminHero
        icon={CircleHelp}
        badge="Help center"
        title="FAQs"
        description="Questions and answers shown to customers in the app."
        actions={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            className="!border-0 !bg-white !text-brand-dark hover:!bg-violet-50"
          >
            Add FAQ
          </Button>
        }
      />

      <AdminStats
        items={[
          { label: "Total FAQs", value: data.length, icon: CircleHelp },
          { label: "Showing", value: filteredData.length, icon: Layers },
          {
            label: "With description",
            value: data.filter((f) => f.description).length,
            icon: FileText,
          },
          {
            label: "With summary",
            value: data.filter((f) => f.shortDescription).length,
            icon: FileText,
          },
        ]}
      />

      <AdminSearchBar
        placeholder="Search title or answer text…"
        value={search}
        onChange={setSearch}
      />

      <PeopleDataTable
        columns={columns}
        rows={filteredData}
        rowKey="_id"
        renderActions={(row) => (
          <>
            <ActionBtn variant="edit" onClick={() => handleEdit(row)}>
              <Pencil size={14} />
              Edit
            </ActionBtn>
            <ActionBtn
              variant="delete"
              onClick={() => handleDelete(row._id, row.title)}
            >
              <Trash2 size={14} />
              Delete
            </ActionBtn>
          </>
        )}
        emptyTitle="No FAQs found"
        emptyHint="Add an FAQ or clear your search."
      />

      <Modal
        title={editingUser ? "Edit FAQ" : "Add FAQ"}
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={800}
      >
        <AddUserForm
          form={form}
          onFinish={handleSave}
          initialValues={editingUser || {}}
        />
      </Modal>
    </AdminPageLayout>
  );
};

export default FaqPage;
